// ============================================================================
// Normalización de teléfonos a `wa_id` (C4 · importación de contactos).
//
// Por qué existe un módulo entero para esto: el `wa_id` es la LLAVE del contacto
// (`unique (tenant_id, wa_id)`). Si el mismo cliente entra una vez como
// "3001234567" y otra como "+57 300 123 4567", quedan DOS contactos, dos
// conversaciones y dos historiales — y nadie se entera hasta que el cliente
// reclama que ya había hablado con alguien.
//
// Alcance deliberado: Colombia. Un número de otro país se acepta solo si ya
// viene con indicativo internacional; lo que NO se hace nunca es adivinar.
// Rechazar con motivo es infinitamente mejor que importar basura.
// ============================================================================

/** Indicativo de Colombia. Cuando entre un tenant de otro país, sale de `tenants.config`. */
const PAIS_POR_DEFECTO = "57";

export interface TelefonoNormalizado {
  ok: boolean;
  /** Id de WhatsApp: solo dígitos, con indicativo. `573001234567`. */
  waId?: string;
  /** Para mostrar: `+57 300 123 4567`. */
  e164?: string;
  motivo?: string;
}

/**
 * Convierte lo que sea que haya escrito el negocio en su Excel a un `wa_id`.
 * Acepta espacios, guiones, paréntesis, puntos y el prefijo internacional en
 * cualquiera de sus formas (`+57`, `0057`, `57`).
 */
export function normalizarTelefono(crudo: string | null | undefined): TelefonoNormalizado {
  const texto = String(crudo ?? "").trim();
  if (!texto) return { ok: false, motivo: "sin teléfono" };

  // Notación científica: Excel convierte 3001234567 en 3,00123E+09 al guardar
  // como CSV. Es basura irrecuperable — el número real ya se perdió.
  if (/e\+?\d/i.test(texto)) {
    return { ok: false, motivo: "Excel lo guardó como número científico y se perdieron los dígitos; formatea la columna como texto" };
  }

  let d = texto.replace(/\D/g, "");
  if (!d) return { ok: false, motivo: "no tiene dígitos" };

  // `0057…` y `00 57…`: prefijo internacional a la vieja usanza.
  if (d.startsWith("00")) d = d.slice(2);

  // Colombia: los móviles empiezan por 3 y son 10 dígitos.
  if (d.length === 10 && d.startsWith("3")) return armar(PAIS_POR_DEFECTO + d);

  // Ya trae el 57 delante.
  if (d.length === 12 && d.startsWith(PAIS_POR_DEFECTO + "3")) return armar(d);

  // `03001234567`: el 0 de marcación nacional que sobra.
  if (d.length === 11 && d.startsWith("03")) return armar(PAIS_POR_DEFECTO + d.slice(1));

  // Fijos: no tienen WhatsApp. Es un rechazo con nombre propio porque es el
  // caso más común en una base vieja de clientes.
  if (d.length === 7 || (d.length === 8 && !d.startsWith("3"))) {
    return { ok: false, motivo: "parece un fijo: WhatsApp necesita un celular" };
  }
  if (d.length === 10 && !d.startsWith("3")) {
    return { ok: false, motivo: "no parece un celular colombiano (los celulares empiezan por 3)" };
  }

  // Otro país, pero solo si viene con indicativo y largo plausible (E.164 = 8 a 15).
  if (d.length >= 11 && d.length <= 15 && !d.startsWith(PAIS_POR_DEFECTO)) return armar(d);

  return { ok: false, motivo: `no reconozco este número (${d.length} dígitos)` };
}

function armar(waId: string): TelefonoNormalizado {
  return { ok: true, waId, e164: formatear(waId) };
}

/** `573001234567` → `+57 300 123 4567`. Solo cosmética. */
export function formatear(waId: string): string {
  if (waId.length === 12 && waId.startsWith(PAIS_POR_DEFECTO)) {
    const n = waId.slice(2);
    return `+57 ${n.slice(0, 3)} ${n.slice(3, 6)} ${n.slice(6)}`;
  }
  return `+${waId}`;
}
